import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import {
  checkFormEqualWhenDirty,
  checkFormListValidateWhenDirty,
  isEmptyAllItemObject,
  isEmptyArray,
  isStringEmpty,
  parseEmptyArray,
  stringEmptyDefault,
} from '@common/helpers';
import {
  ANA_NAME_CODE,
  BLIND,
  DEAF,
  MailDestinationType,
  PHONE_EXTENSION_JP,
  PhoneDestinationType,
  SecondContactDestinationType,
  SmsChangeWarningMessage,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import {
  NotificationInformationState,
  PassengerInformationState,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.state';
import {
  ContactInformationData,
  MileageProgramInformationData,
  PassengerInformationData,
  PassengerInformationParts,
  PassengerType,
  PassportInformationData,
  PassportInformationParts,
  PhoneNumberCountryType,
  SecondContact,
  RegulatoryDetailsDestinationAddress,
  RepresentativeInformationData,
} from '@common/interfaces';
import { FrequentFlyerCards } from '@common/interfaces/frequent-flyer-cards';
import { MMileageProgramI18n } from '@common/interfaces/master/m_mileage_program_i18n';
import { PassengerInformationInputModalStoreService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal-store.service';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { PassengerInformationService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information.service';
import { SelectComponent } from '@lib/components';
import { AswValidators, convertStringToDate, isAlphaNumeric, isNumeric } from '@lib/helpers';
import { ValidatePhoneNumberOptions, ValidationErrorInfo } from '@lib/interfaces';
import { DateFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService, SystemDateService } from '@lib/services';
import { Subscription } from 'rxjs';
import { IFlyMemberInfo, IFlyMemberInfoProfileDetails } from 'src/sdk-member';
import { GetOrderResponseData, GetOrderResponseDataContactsRepresentativePhonesInner } from 'src/sdk-servicing';
import { ArrivalDepartureNoticeInformationComponent } from './additional-information/arrival-departure-notice-information/arrival-departure-notice-information.component';
import { ImmigrationInformationComponent } from './additional-information/immigration-information/immigration-information.component';
import { PassportInformationComponent } from './additional-information/passport-information/passport-information.component';
import { SupportInformationComponent } from './additional-information/support-information/support-information.component';
// 型：登録済み or 区切り線 or 未登録
type MileageProgramOption = MMileageProgramI18n | { isSeparator: true };

/**
 * passenger-information
 * 搭乗者情報ブロック
 */
@Component({
  selector: 'asw-passenger-information',
  templateUrl: './passenger-information.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassengerInformationComponent implements OnDestroy, OnChanges {
  private readonly _subscription: Subscription = new Subscription();

  @Input()
  get data(): PassengerInformationData | undefined {
    return this._data;
  }
  set data(value: PassengerInformationData | undefined) {
    this._data = value;
    this.onInit();
  }
  private _data?: PassengerInformationData;

  @Output()
  dataChange: EventEmitter<PassengerInformationData> = new EventEmitter<PassengerInformationData>();

  @Input()
  get parts(): PassengerInformationParts | undefined {
    return this._parts;
  }
  set parts(value: PassengerInformationParts | undefined) {
    this._parts = value;
    this.onInit();
  }
  private _parts?: PassengerInformationParts;

  @Input()
  get isOpen() {
    return this._isOpen;
  }
  set isOpen(value: boolean) {
    this._isOpen = value;
    this._change.markForCheck();
  }
  private _isOpen: boolean = false;

  @Output()
  isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() passengerIndex: number = -1;

  @Input() secondContentOfFirstPassenger?: SecondContact;

  @Input() isSecondContentRegistAfterReservationOfFirstPassenger?: boolean;

  @Input() addressDuringStayOfFirstPassenger?: RegulatoryDetailsDestinationAddress;

  @Input() representativeData?: RepresentativeInformationData;

  @Input() isDisplayRepresentative: boolean = false;

  @Input() isDisplayReflectBtn: boolean = false;

  @ViewChild('immigrationInfoComponent') immigrationInfoComponent?: ImmigrationInformationComponent;

  @ViewChild('passportInfoComponent') passportInfoComponent?: PassportInformationComponent;

  @ViewChild('supportInfoComponent') supportInfoComponent?: SupportInformationComponent;

  @ViewChild('arrivalDepartureNoticeInfoComponent')
  arrivalDepartureNoticeInfoComponent?: ArrivalDepartureNoticeInformationComponent;

  public PASSENGER_FORMS = {
    PASSPORT_NUMBER: 'passportNumber',
    MILEAGE_PROGRAM_NAME: 'mileageProgramName',
    MILEAGE_PROGRAM_NUMBER: 'mileageProgramNumber',
    MAIL_DESTINATION: 'mailDestination',
    SECOND_CONTACT_DESTINATION: 'secondContactDestination',
    MAIL_ADDRESS: 'mailAddress',
    MAIL_ADDRESS_CONFIRM: 'mailAddressConfirm',
    SMS_DESTINATION: 'smsDestination',
    PHONE_COUNTRY_CODE: 'phoneCountryCode',
    PHONE_NUMBER: 'phoneNumber',
    SECOND_CONTACT_PHONE_COUNTRY_CODE: 'secondContactPhoneCountryCode',
    SECOND_CONTACT_PHONE_NUMBER: 'secondContactPhoneNumber',
    SECOND_CONTACT_PHONE_OWNER: 'secondContactPhoneOwner',
  };

  public formGroup = new FormGroup<{ [key: string]: FormControl }>({
    [this.PASSENGER_FORMS.PASSPORT_NUMBER]: new FormControl(),
    [this.PASSENGER_FORMS.MAIL_DESTINATION]: new FormControl('', [
      AswValidators.required({ params: NotificationInformationState.ContactEmail }),
    ]),
    // (50.1.1.5.1) 搭乗者メールアドレス
    [this.PASSENGER_FORMS.MAIL_ADDRESS]: new FormControl('', [
      AswValidators.required({ params: PassengerInformationState.Params.Email }),
      AswValidators.email({ params: PassengerInformationState.Params.Email }),
    ]),
    // (50.1.1.5.2) 搭乗者メールアドレス(確認用)
    [this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM]: new FormControl('', [
      AswValidators.required({ params: PassengerInformationState.Params.ConfirmEmail }),
      AswValidators.email({ params: PassengerInformationState.Params.ConfirmEmail }),
    ]),
  });

  public renderComponent: boolean = false;
  public isDisplaySendMail: boolean = false;
  public isDisplaySendSMS: boolean = false;
  public isSecondContactSendToIndividual: boolean = false;
  public countryPhoneExtension: string = '';
  public secondContactCountryPhoneExtension: string = '';
  public dateOfBirth: string = '';
  public selectedBirthdayYmd: Date | null = null;
  public birthDateErrorMsg: string | ValidationErrorInfo = '';
  public phoneValidatorOption: ValidatePhoneNumberOptions = {
    telCountryCode: this.countryPhoneExtension,
    isSmsSend: true,
    params: PassengerInformationState.Params.ContactNumber,
  };
  public secondContactPhoneValidatorOption: ValidatePhoneNumberOptions = {
    telCountryCode: this.secondContactCountryPhoneExtension,
    isSmsSend: true,
    params: PassengerInformationState.Params.ContactNumber,
  };
  public isVisaOrGreenCardSelected: boolean = false;
  public isStay: boolean | undefined = undefined;
  public isNoConfirmAddress: boolean | undefined = undefined;
  public isImmigrationInfoRegistered: boolean | undefined = undefined;
  private isInitBirthDate = true;
  private readonly _mileagePrograms: MMileageProgramI18n[] = [];
  public prePhoneEx: string = '';

  get profileDetail(): IFlyMemberInfoProfileDetails {
    // 代表者のデータを取得
    const profileDetail = this._s04StoreSvc.representativeProfileDetail() ?? {
      customerNumber: '',
      individualInfo: { dateOfBirth: '' },
    };
    return profileDetail;
  }

  get memberInformation(): IFlyMemberInfo {
    return this._s04StoreSvc.memberInformation() ?? { profileDetails: {}, programDetails: [] };
  }

  get getNotRegisteredLabel(): string {
    return this._staticMsg.transform('label.hyphen');
  }

  get passengerTypeLabel(): string {
    return this._staticMsg.transform(this.parts?.passengerTypeLabel ?? '');
  }

  get registrationLabel(): string {
    return this._staticMsg.transform(this.parts?.registrationLabel ?? '');
  }

  get getStatusFoldingWheelchair(): string {
    // [6.21.1] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isFoldable = “2“の場合、折りたたみ可能の文言を表示する。
    if (this.wheelchairIsFoldable === '2') {
      return this._staticMsg.transform('label.foldable');
    }
    // [6.21.2] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isFoldable = “1“の場合、折りたたみ不可の文言を表示する。
    if (this.wheelchairIsFoldable === '1') {
      return this._staticMsg.transform('label.notFoldable');
    }
    // [6.21.3] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isFoldable = “0“、または予約基本情報取得API応答.data.passengerSupport.wheelchair.isFoldableが存在しない場合は未登録の旨を表示する。
    return this.getNotRegisteredLabel;
  }

  get getWheelchairAvailability(): string {
    // [6.20.1] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isNeeded = “1“ の場合、車いす持ち込みありの文言。
    if (this.wheelchairIsNeeded === '1') {
      return this._staticMsg.transform('label.willBeCheckedIn');
    }
    // [6.20.2] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isNeeded=“0“、車いす持ち込みなしの文言。
    if (this.wheelchairIsNeeded === '0') {
      return this._staticMsg.transform('label.willNotBeCheckedIn');
    }
    // [6.20.3] 予約基本情報取得API応答.data.passengerSupport.wheelchair.isNeededが存在しない場合は未登録の旨を表示する。
    return this.getNotRegisteredLabel;
  }

  get basicReservationWheelchairType(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '57')?.attributeValue ?? '';
    return (
      {
        '0': '',
        '1': 'battery',
        '2': 'manual',
      }[attributeValue] ?? ''
    );
  }

  get getWheelchairType(): string {
    // [6.24.2] 予約基本情報取得API応答.data.passengerSupport.wheelchair.type = ”1”(電動)の場合、電動である旨を表示する。
    if (this.basicReservationWheelchairType === 'battery') {
      return this._staticMsg.transform('label.electric');
    }
    // [6.24.1] 予約基本情報取得API応答.data.passengerSupport.wheelchair.type =”2”(手動)の場合、手動である旨を表示する。
    if (this.basicReservationWheelchairType === 'manual') {
      return this._staticMsg.transform('label.manual');
    }
    // [6.24.3] 予約基本情報取得API応答.data.passengerSupport.wheelchair.type = ”0”、または予約基本情報取得API応答.data.passengerSupport.wheelchair.typeが存在しない場合は未登録の旨を表示する。
    return this.getNotRegisteredLabel;
  }

  get walkingAbilityDisplay(): string {
    const pd006 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_006' && data.lang === this._s04CommonSvc.lang
    );
    if (isStringEmpty(this.degreeSpecialServiceRequestCode)) {
      return this.getNotRegisteredLabel;
    }
    return pd006?.find((walking) => walking?.value === this.degreeSpecialServiceRequestCode)?.display_content ?? '';
  }

  get batteryTypeDisplay(): string {
    const pd960 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_960' && data.lang === this._s04CommonSvc.lang
    );
    /**
     * 予約基本情報取得API応答.data.passengerSupport.wheelchair.batteryTypeに応じ、「イベントID(1)」で取得したASWDB(マスタ)から取得した文言を表示する。
     * 予約基本情報取得API応答.data.passengerSupport.wheelchair.batteryType = ”0”、または予約基本情報取得API応答.data.passengerSupport.wheelchair.batteryTypeが存在しない場合は未登録の旨を表示する。
     */
    return pd960?.find((battery) => battery?.value === this.batteryType)?.display_content ?? this.getNotRegisteredLabel;
  }

  get batteryType(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '58')?.attributeValue ?? '';
    return attributeValue;
  }

  get degreeSpecialServiceRequestCode(): string {
    const customerDynamicAttribute = this.profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '50')?.attributeValue ?? '';
    return (
      {
        R: 'WCHR',
        S: 'WCHS',
        C: 'WCHC',
      }[attributeValue] ?? ''
    );
  }

  get wheelchairSizeLabel(): string {
    // [6.22.4] 予約基本情報取得API応答.data.passengerSupport.wheelchair.depth、予約基本情報取得API応答.data.passengerSupport.wheelchair.width、予約基本情報取得API応答.data.passengerSupport.wheelchair.heightが存在しない場合は未登録の旨を表示する。
    if (
      isStringEmpty(this.wheelchairDepth) &&
      isStringEmpty(this.wheelchairWidth) &&
      isStringEmpty(this.wheelchairHeight)
    ) {
      return this.getNotRegisteredLabel;
    }
    /** [6.22.1 6.22.2 6.22.3]
     * 以下の情報を含む文言を表示する。
     * 1.予約基本情報取得API応答.data.passengerSupport.wheelchair.depth
     * 2.予約基本情報取得API応答.data.passengerSupport.wheelchair.width
     * 3.予約基本情報取得API応答.data.passengerSupport.wheelchair.height
     */
    return this._staticMsg.transform('label.wheelchairSize', {
      '0': this.wheelchairDepth ?? '',
      '1': this.wheelchairWidth ?? '',
      '2': this.wheelchairHeight ?? '',
    });
  }

  get wheelchairWeightLabel(): string {
    // 予約基本情報取得API応答.data.passengerSupport.wheelchair.weightが存在しない場合は未登録の旨を表示する。
    if (isStringEmpty(this.wheelchairWeight)) {
      return this.getNotRegisteredLabel;
    }
    // 予約基本情報取得API応答.data.passengerSupport.wheelchair.weightを含む文言を表示する。
    return this._staticMsg.transform('label.wheelchairWeight', {
      '0': this.wheelchairWeight ?? '',
    });
  }

  get wheelchairIsNeeded(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '56')?.attributeValue ?? '';
    return attributeValue;
  }

  get wheelchairIsFoldable(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '59')?.attributeValue ?? '';
    return attributeValue;
  }

  get wheelchairHeight(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '60')?.attributeValue ?? '';
    return attributeValue;
  }

  get wheelchairWidth(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '61')?.attributeValue ?? '';
    return attributeValue;
  }

  get wheelchairDepth(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '62')?.attributeValue ?? '';
    return attributeValue;
  }

  get wheelchairWeight(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '63')?.attributeValue ?? '';
    return attributeValue;
  }

  get blind(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '51')?.attributeValue ?? '';
    return (
      {
        '0': '',
        '1': BLIND,
      }[attributeValue] ?? ''
    );
  }

  get deaf(): string {
    const profileDetail = this.profileDetail;
    const customerDynamicAttribute = profileDetail?.customerDynamicAttribute ?? [];
    const attributeValue = customerDynamicAttribute.find((x) => x.attributeCode === '52')?.attributeValue ?? '';
    return (
      {
        '0': '',
        '1': DEAF,
      }[attributeValue] ?? ''
    );
  }

  get birthdayDateFrom(): Date {
    return this.parts?.selectDate?.dateFrom ? this.parts.selectDate.dateFrom : new Date();
  }

  get birthdayDateTo(): Date {
    return this.parts?.selectDate?.dateTo ? this.parts.selectDate.dateTo : new Date();
  }

  get birthdayInitialDate(): { year?: number; month?: number; day?: number } {
    return this.parts?.selectDate?.initialDate
      ? this.parts.selectDate.initialDate
      : {
          day: undefined,
          month: undefined,
          year: undefined,
        };
  }

  get pnr(): GetOrderResponseData | undefined {
    return this._s04StoreSvc.pnr();
  }

  get travelerContacts() {
    return this.pnr?.contacts?.[this.data?.travelerId ?? ''];
  }

  get isDisplaySupportInfoLabel(): boolean {
    const isSupportRegistered =
      (this.pnr?.travelerSupports as any)?.[this.data?.travelerId ?? '']?.isSupportRegistered ?? false;
    const customerDynamicAttribute = (this.profileDetail?.customerDynamicAttribute ?? []).filter(
      (attr) =>
        attr.dynamicAttributeGroupName && attr.dynamicAttributeGroupName.toLocaleLowerCase() === 'support information'
    );
    return (
      Boolean(this.parts?.supportInformationParts?.isSupportInformationEditable) &&
      customerDynamicAttribute.length > 0 &&
      !isSupportRegistered &&
      this.isDisplaySupportInfo &&
      this.isNotPassengerINF
    );
  }

  /**
   * [1.15] サポート情報表示
   * 以下のいずれかを満たす場合、PNRのサポート情報をラベル表示する。
   */
  get isDisplaySupportInfo(): boolean {
    // 1.
    const condition1 =
      // PNR情報取得API応答.data.air.bounds.flights[]リストの中にisDepartedWithin24Hours=trueであるフライトが存在しない、
      !(
        this.pnr?.air?.bounds?.some((bound) => bound.flights?.some((flight) => flight.isDepartedWithin24Hours)) ?? false
      ) &&
      // かつ
      //   PNR情報取得API応答.data.orderEligibilities.travelerInput.isPassengerSupportEligible=true
      //   かつ　PNR情報取得API応答.data.nextAction.acknowledgeOrSelfReaccommodation=false の場合
      this.pnr?.orderEligibilities?.travelerInput?.isPassengerSupportEligible === true &&
      this.pnr?.nextActions?.acknowledgeOrSelfReaccommodation === false;
    // 2.PNR情報取得API応答.data.travelerSupports.＜当該travelers.id>.isSupportRegistered=true
    const condition2 = (this.pnr?.travelerSupports as any)?.[this.data?.travelerId ?? '']?.isSupportRegistered ?? false;

    return condition1 || condition2;
  }

  get nhGroupAndArrivalUS(): boolean {
    return (
      this.pnr?.air?.bounds?.some((bound) =>
        bound?.flights?.some((flight) => flight?.isNhGroupOperated && flight?.arrival?.country2letterCode === 'US')
      ) ?? false
    );
  }

  get isDisplayPassengerEmailDestination(): boolean {
    // [8.3.3] 搭乗者メールアドレス編集可否＝falseの場合、非表示とする
    return this.pnr?.orderType?.isGroupBooking === false;
  }

  constructor(
    private readonly _change: ChangeDetectorRef,
    private readonly _staticMsg: StaticMsgPipe,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService,
    private readonly _s04StoreSvc: PassengerInformationInputModalStoreService,
    private readonly _service: PassengerInformationService,
    private readonly _common: CommonLibService,
    private readonly _dateFormatPipe: DateFormatPipe,
    private _systemDateSvc: SystemDateService
  ) {}

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  // 提携では「年齢」「生年月日」が非表示になるため処理を追加。
  // ngAfterContentChecked は非推奨なので処理追記は可能な限り避けること。
  ngAfterContentChecked(): void {
    this._change.markForCheck();
  }

  /**
   * 初期化処理
   */
  onInit() {
    if (!this.data || !this.parts) {
      return;
    }

    this.parts.immigrationInformationParts.passengerIndex = this.passengerIndex;

    if (this.passengerIndex > 0) {
      this.parts.immigrationInformationParts.addressDuringStayOfFirstPassenger = this.addressDuringStayOfFirstPassenger;
    }

    const pnrPassports = this.data.passports;
    this.parts.selectDate.dateFrom ??= pnrPassports?.[0]?.birthDate
      ? convertStringToDate(pnrPassports?.[0]?.birthDate)
      : null;

    if (this.parts.selectDate?.dateTo === null) {
      this.parts.selectDate.dateFrom ??= new Date();
    }

    this.parts.selectDate.dateTo ??= new Date();
    const pnrContact = this.data.contact;

    this.initPassengerContactForms(pnrContact);

    this._change.markForCheck();
  }

  /**
   * 提供された連絡先情報を使用して乗客連絡フォームを初期化します。
   * @param contact 連絡先情報のデータ。
   */
  initPassengerContactForms(contact?: ContactInformationData) {
    // [8.4.1] 画面.搭乗者メール送付先＝”1”(個人に送る)の場合、PNR情報取得API応答.data.contacts./(\d+)/.emails.addressを表示する
    if (contact?.passengerMailDestination === MailDestinationType.INDIVIDUAL) {
      this.isDisplaySendMail = true;
    }

    this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION].setValue(
      contact?.passengerMailDestination ?? MailDestinationType.INDIVIDUAL
    );
    this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS].setValue(contact?.passengerMailAddress ?? '');
    this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM].setValue(
      contact?.passengerMailAddressConfirm ?? ''
    );
  }

  /**
   * 旅客情報データを更新し、更新したデータを出力します。
   * @param requiredCheckError 必要なチェックエラー検証を実行するかどうかを示します。
   */
  update(requiredCheckError: boolean = false) {
    if (!this.data) {
      return;
    }

    this.updateContactData();
    this.updateBasicInfoData();

    this.data.isError = this.validatePassengerInfoData(requiredCheckError);

    this._change.markForCheck();
    this.dataChange.emit(this.data);
  }

  /**
   * 乗客情報の連絡先関連データを更新します。
   */
  updateContactData() {
    if (!this.data) {
      return;
    }

    // (14.1.1)	画面.搭乗者メール送付先＝”0”(代表者に送る)の場合、画面.搭乗者メールアドレスを非表示とする。
    if (this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION].value === MailDestinationType.REPRESENTATIVE) {
      this.data.contact.passengerMailAddress = this.representativeData?.emails?.[0]?.email ?? '';
      this.data.contact.passengerMailAddressConfirm = this.representativeData?.emails?.[0]?.email ?? '';
    }

    // (14.1.2)	上記以外の場合、画面.搭乗者メールアドレスに””(空欄)を設定し、表示する。
    if (this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION].value === MailDestinationType.INDIVIDUAL) {
      this.data.contact.passengerMailAddress = this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS].value ?? '';
      this.data.contact.passengerMailAddressConfirm =
        this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM].value ?? '';
    }
  }

  /**
   * 旅客情報の基本情報関連データを更新します。
   */
  updateBasicInfoData() {
    if (!this.data) {
      return;
    }

    if (this.selectedBirthdayYmd) {
      this.data.basicInformation.selectYmd = this.selectedBirthdayYmd;
    }
  }

  /**
   * 乗客情報データを検証します。
   * @param requiredCheckError 必要なチェックエラー検証を実行するかどうかを示します。
   * @returns 検証が成功したかどうかを示すブール値。
   */
  validatePassengerInfoData(requiredCheckError: boolean): boolean {
    let result: boolean = false;
    result = checkFormListValidateWhenDirty(
      [
        this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION],
        ...(this.isDisplaySendMail && this.isNotPassengerINF && this.parts?.isPassengerEmailEditable
          ? [
              this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS],
              this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM],
            ]
          : []),
      ],
      requiredCheckError
    );

    if (this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION].value === MailDestinationType.INDIVIDUAL) {
      if (
        (!isStringEmpty(this.data?.contact?.passengerMailAddressConfirm) || requiredCheckError) &&
        checkFormEqualWhenDirty(
          this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS],
          this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM],
          PassengerInformationState.Errors.ConfirmEmail
        )
      ) {
        result = true;
      }
    }

    if (
      (this.data?.basicInformation?.selectYmd === null || this.data?.basicInformation?.selectYmd === undefined) &&
      this.parts?.isDisplayBasicInformation
    ) {
      this.birthDateErrorMsg = {
        errorMsgId: 'E0001',
        params: {
          key: '0',
          value: 'label.dateOfBirthForMandatory',
        },
      };
      result = true;
    }
    return result;
  }

  /**
   * (9) 開くボタン押下処理　※搭乗者情報ヘッダパーツ
   * (32) 閉じるボタン押下処理　※搭乗者情報詳細グループ
   */
  onClickShutter() {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);

    if (!this.renderComponent) {
      this.renderComponent = true;
    }
  }

  /**
   * (10) 予約基本情報詳細(搭乗者)表示ボタン押下処理　※予約基本情報(搭乗者)グループ
   * (11) キャンセルボタン押下処理　※予約基本情報(搭乗者)グループ
   */
  onClickCheckDetails() {
    if (!this.parts) {
      return;
    }

    this.parts.isOpenBasicReservationDetails = !this.parts.isOpenBasicReservationDetails;
    this._change.markForCheck();
  }

  /**
   * (12) 反映(搭乗者情報)ボタン押下処理　※予約基本情報(搭乗者)グループ
   */
  onClickEnterUsualInfo() {
    // (12.1) 以下の条件で当ブログの各項目の値を更新する
    if (this.isDisplaySupportInfoLabel) {
      this.supportInfoComponent?.setSupportInfo();
    }

    // (12.2) 画面.予約基本情報詳細(搭乗者)グループを閉じ、非表示とする。
    this.onClickCheckDetails();

    this.dataChange.emit(this.data);
    this._change.markForCheck();
  }

  /**
   * リスニングデータ変更イベント
   */
  onAdditionDataChange() {
    this.dataChange.emit(this.data);
  }

  /**
   * 外部呼出し用 強制refresh
   */
  refreshForce() {
    this.onInit();
  }

  /**
   * 外部呼出し用
   * 外部からの呼び出し時に「第2緊急連絡先」が「搭乗者1と同じ」を選択している場合、更新処理を行う
   * */
  updateSecondContactWithFirstPassenger() {
    const secondContactDestination = this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_DESTINATION].value;

    if (!secondContactDestination) {
      return;
    }

    if (secondContactDestination === SecondContactDestinationType.SAME_PASSENGER_1) {
      // 搭乗者1と同じ
      if (!this.data?.contact?.secondContact) {
        return;
      }
      if (!this.secondContentOfFirstPassenger) {
        return;
      }

      // データ本体に値コピー
      this.data.contact.secondContact = {
        countryPhoneExtension: this.secondContentOfFirstPassenger.countryPhoneExtension,
        countryCode: this.secondContentOfFirstPassenger.countryCode,
        phonesNumber: this.secondContentOfFirstPassenger.phonesNumber,
        ownerOfPhone: this.secondContentOfFirstPassenger.ownerOfPhone,
      };

      this.update();
    }
  }

  /**
   * 外部呼出し用 強制update
   * */
  updateForce(requiredCheckError: boolean = false) {
    this.update(requiredCheckError);
    if (
      this.parts?.isDisplayArrivalDepartureNotify &&
      this.isNotPassengerINF &&
      this.arrivalDepartureNoticeInfoComponent?.isOpen
    )
      this.arrivalDepartureNoticeInfoComponent?.update(requiredCheckError);
  }

  /** (14) 搭乗者メール送信先の値変更処理　※搭乗者連絡先情報パーツ */
  onChangeMailDestination() {
    this.isDisplaySendMail =
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_DESTINATION].value !== MailDestinationType.REPRESENTATIVE;
    if (this.isDisplaySendMail) {
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS].enable();
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM].enable();
    } else {
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS].disable();
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM].disable();
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS].setErrors(null);
      this.formGroup.controls[this.PASSENGER_FORMS.MAIL_ADDRESS_CONFIRM].setErrors(null);
    }
  }

  /**
   * 選択したメール宛先タイプに基づいて、メールが個々の乗客 2 に送信されるかどうかを更新します。
   */
  onChangeSecondContactDestination() {
    const secondContactDestination = this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_DESTINATION].value;

    if (!secondContactDestination) {
      return;
    }

    if (secondContactDestination === SecondContactDestinationType.SAME_PASSENGER_1) {
      // Same as passenger 1
      // この時点では何もしない
    }

    this.isSecondContactSendToIndividual = secondContactDestination === SecondContactDestinationType.INDIVIDUAL;

    if (this.parts) {
      this.parts.isSecondContactRegistAfterReservation =
        secondContactDestination === SecondContactDestinationType.REGISTER_LATER;
    }

    if (this.isSecondContactSendToIndividual) {
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_COUNTRY_CODE].enable();
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_NUMBER].enable();
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_OWNER].enable();

      if (this.data?.contact?.secondContact.countryPhoneExtension === '') {
        this.data.contact.secondContact.countryPhoneExtension = this.prePhoneEx;
        this.data.contact.secondContact.countryCode =
          this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_COUNTRY_CODE].value ?? '';
        this.data.contact.secondContact.phonesNumber =
          this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_NUMBER].value ?? '';
        this.data.contact.secondContact.ownerOfPhone =
          this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_OWNER].value ?? '';
      }
    } else {
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_COUNTRY_CODE].disable();
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_NUMBER].disable();
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_OWNER].disable();
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_COUNTRY_CODE].setErrors(null);
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_NUMBER].setErrors(null);
      this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_PHONE_OWNER].setErrors(null);

      // データ本体を初期化
      if (
        this.data?.contact?.secondContact &&
        this.data.contact.secondContact.countryPhoneExtension !== '' &&
        this.secondContentOfFirstPassenger
      ) {
        this.prePhoneEx = this.data.contact.secondContact.countryPhoneExtension;

        this.data.contact.secondContact = {
          countryPhoneExtension: '',
          countryCode: '',
          phonesNumber: '',
          ownerOfPhone: '',
        };
      }
    }
  }

  /**
   * 生年月日変更処理
   * @param date 変更された生年月日
   */
  onChangeBirthday(date: Date | null) {
    this.selectedBirthdayYmd = date;

    if (this.selectedBirthdayYmd !== null) {
      this.isInitBirthDate = false;
      this.birthDateErrorMsg = '';
      this._change.detectChanges();
    }

    if (!this.isInitBirthDate && this.selectedBirthdayYmd === null) {
      this.birthDateErrorMsg = { errorMsgId: 'E0001', params: { key: '0', value: 'label.dateOfBirthForMandatory' } };
    }

    this.update();
  }

  getNotificationEmail() {
    return this.pnr?.tasks?.isUsContactRequired ? 'heading.passengerEmailForEnteringUs' : 'label.passengerEmail';
  }

  get isNotPassengerINF(): boolean {
    return this.data?.passengerTypeCode !== PassengerType.INF;
  }

  // (代表者連絡先情報.emailsの要素数≧2の場合、非表示)
  isDisplaySendToRrepresentativeMailAddress(): boolean {
    return this.representativeData?.emails ? this.representativeData?.emails?.length < 2 : false;
  }

  // [8.14.1] 以下の条件すべてに該当する場合、表示する。ただし、2件目緊急連絡先電話番号が複数登録済みの場合、非表示とする。
  isDisplaySecondContactDestination(): boolean {
    return (
      //当該flights.isNhGroupOperated=true、かつ当該flights.arrival.country2letterCode=”US”
      Boolean(this.pnr?.tasks?.isUsContactRequired) &&
      // PNR情報取得API応答.data.orderType.isGroupBooking=false
      !Boolean(this.pnr?.orderType?.isGroupBooking) &&
      // 2件目緊急連絡先電話番号が複数登録済みの場合、非表示とする。
      this.travelerContacts?.secondContact?.length < 2
    );
  }

  // [8.14.2.3] 後から登録（PNRに未登録の場合のみ表示）
  isDisplayRegistAfterReservation(): boolean {
    const travelerId = this.data?.travelerId;
    return !travelerId ? true : !Boolean(this.pnr?.contacts?.[travelerId]?.secondContact?.[0]?.phones?.number);
  }

  // [8.14.2.1] 1人目の搭乗者と同じ(2人目以降の搭乗者かつ、1人目の搭乗者の2件目緊急連絡先電話番号が複数登録されていない場合のみ表示)
  isDisplaySameAsFirstPassenger(): boolean {
    return this.passengerIndex > 0 && this.pnr?.contacts?.[this._service.firstTravelerId]?.secondContact?.length < 2;
  }

  // 1人目の搭乗者が「後で登録する」を選択している場合は、
  // 「搭乗者1と同じ」を非表示にする
  isDisabledSameAsFirstPassenger(): boolean {
    return this.isSecondContentRegistAfterReservationOfFirstPassenger !== undefined
      ? this.isSecondContentRegistAfterReservationOfFirstPassenger
      : true;
  }

  // @import要素が更新された際に自動で呼び出されます。
  // 1.搭乗者1の第2緊急連絡先選択が「後で登録する」に変更され、
  //  当搭乗者の第2緊急連絡先選択が「搭乗者1と同じ」の場合に、
  //  「個別に登録する」に変更するようにします
  ngOnChanges(changes: SimpleChanges): void {
    // isSecondContentRegistAfterReservationOfFirstPassengerが変わったタイミング
    if (changes['isSecondContentRegistAfterReservationOfFirstPassenger'] && this.isDisplaySameAsFirstPassenger()) {
      if (this.isDisabledSameAsFirstPassenger()) {
        const secondContactDestination = this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_DESTINATION].value;
        if (secondContactDestination === SecondContactDestinationType.SAME_PASSENGER_1) {
          this.formGroup.controls[this.PASSENGER_FORMS.SECOND_CONTACT_DESTINATION].setValue(
            SecondContactDestinationType.INDIVIDUAL
          );

          this.onChangeSecondContactDestination();
        }
      }
    }
  }

  // [8.8.2.2.3] 送信しない
  isDisPlayNotSendBtn(): boolean {
    return !Boolean(this.pnr?.tasks?.isUsContactRequired);
  }

  // [8.8.3] 代表者に送る旨について、以下の表示制御を行う。
  isDisplaySendToRepresentativeSmsBtn(): boolean {
    return this.isDisplayRepresentative && (this.representativeData?.phones ?? [])?.length < 2;
  }

  isDisplaySecondContact(): boolean {
    return Boolean(this.pnr?.tasks?.isUsContactRequired);
  }

  // ※以下の条件すべてに該当する場合、送信しないを選択し非活性とする。
  get isDisabledSMSDestination(): boolean {
    return this.pnr?.contacts?.[this.data?.travelerId!]?.isRefused && this.pnr?.tasks?.isUsContactRequired === false;
  }

  get getTooltipEmail(): string {
    return this.pnr?.tasks?.isUsContactRequired
      ? 'tooltip.bookingAndFlightStatusForEnteringUsGuidance'
      : 'tooltip.passengerEmail';
  }

  get getSmsTooltip(): string {
    return this.pnr?.tasks?.isUsContactRequired
      ? 'tooltip.bookingAndFlightStatusForEnteringUsGuidance'
      : 'tooltip.passengerSms';
  }

  get getBlindLabel(): string {
    if (this.blind === BLIND) {
      return this._staticMsg.transform('label.applicable');
    }
    return this.getNotRegisteredLabel;
  }

  get getDeafLabel(): string {
    if (this.deaf === DEAF) {
      return this._staticMsg.transform('label.applicable');
    }
    return this.getNotRegisteredLabel;
  }
}
